import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { backend } from '@/backend';
import { useCookie } from '@/shared/cookie';
import { useHasAccessToken } from '../auth/api';
import { IS_PRERENDERING } from '../base/constants';
import { useLocation } from '../base/router';
import { useSaveUserStorage, useUserStorage } from '../shared/api';
import {
  COOKIE_OPTIONS,
  getLangFromUrl,
  mergeSettings,
  parseSettings,
  pendingBus,
  pendingKeys,
  SETTINGS_STORAGE_KEY,
  type Settings,
} from './config';

export {
  DEFAULT_OTP_CHANNEL,
  DEFAULT_SETTINGS,
  getSettings,
  SETTINGS_STORAGE_KEY,
  type Settings,
  SUPPORTED_LANGS,
} from './config';

// Writes a settings patch straight to the server, merged into whatever is
// already stored there. Lifecycle-independent (raw `backend`, live token) so it
// can run right after login when `useSettings`'s authenticated state has not yet
// settled. Merging avoids clobbering the user's other server-side preferences.
export const writeSettingToServer = async (patch: Partial<Settings>) => {
  let server: Partial<Settings> = {};
  try {
    const current = await backend('account:get:user-storage/{slug}', {
      slug: SETTINGS_STORAGE_KEY,
    });
    server = parseSettings(current.response?.value as string | null);
  } catch {
    // No server settings yet — start from an empty object.
  }
  await backend('account:post:user-storage/{slug}', {
    slug: SETTINGS_STORAGE_KEY,
    value: JSON.stringify({ ...server, ...patch }),
  });
};

export const useSettings = () => {
  const [, rerender] = useReducer((s) => s + 1, 0);
  const hasAccessToken = useHasAccessToken();

  const [cookie, setCookie] = useCookie(SETTINGS_STORAGE_KEY, COOKIE_OPTIONS);
  const userStorage = useUserStorage(SETTINGS_STORAGE_KEY);
  const saveUserStorage = useSaveUserStorage();
  const [location, navigate] = useLocation();

  useEffect(() => pendingBus.subscribe(rerender), []);

  const cookieSettings = useMemo(() => parseSettings(cookie), [cookie]);

  const serverSettings = useMemo<Partial<Settings> | null>(() => {
    if (!hasAccessToken || !userStorage.data?.value) return null;
    return parseSettings(userStorage.data.value as string);
  }, [hasAccessToken, userStorage.data?.value]);

  const settings = useMemo(
    () => mergeSettings(cookieSettings, serverSettings),
    [cookieSettings, serverSettings],
  );

  const setSettings = useCallback(
    (newSettings: Partial<Settings>) => {
      const mergedSettings: Settings = { ...settings, ...newSettings };
      const changedKeys = Object.keys(newSettings) as (keyof Settings)[];

      setCookie(JSON.stringify(mergedSettings));

      if (hasAccessToken) {
        for (const k of changedKeys) pendingKeys.add(k);
        pendingBus.dispatch();

        saveUserStorage
          .trigger({
            slug: SETTINGS_STORAGE_KEY,
            value: JSON.stringify(mergedSettings),
          })
          .finally(() => {
            for (const k of changedKeys) pendingKeys.delete(k);
            pendingBus.dispatch();
          });
      }

      const urlLang = getLangFromUrl();
      if (
        newSettings.language &&
        urlLang &&
        newSettings.language !== urlLang &&
        location.includes(urlLang)
      ) {
        navigate('public', location.replace(urlLang, newSettings.language), {
          replace: true,
        });
      }

      return mergedSettings;
    },
    [hasAccessToken, location, navigate, saveUserStorage, setCookie, settings],
  );

  return useMemo(
    () => [settings, setSettings] as const,
    [settings, setSettings],
  );
};

// A `/ar` URL on the public workspace is a real language switch, not a
// per-page override: `getLangFromUrl` is public-only, so a language that lives
// only in the merged view is lost on the hard navigation into the app/admin
// builds. Persisting it once per load keeps the cookie and the server in sync
// with the URL. Waits for user-storage so the write cannot clobber the other
// server-side settings with cookie/default values.
export const useUrlLanguageSync = () => {
  const [, setSettings] = useSettings();
  const hasAccessToken = useHasAccessToken();
  const userStorage = useUserStorage(SETTINGS_STORAGE_KEY);
  const [cookie] = useCookie(SETTINGS_STORAGE_KEY, COOKIE_OPTIONS);
  const isSynced = useRef(false);

  const urlLang = getLangFromUrl();
  const isSettled = !hasAccessToken || !userStorage.isLoading;
  const storedLang =
    (hasAccessToken
      ? parseSettings(userStorage.data?.value as string).language
      : undefined) ?? parseSettings(cookie).language;

  useEffect(() => {
    if (IS_PRERENDERING || isSynced.current || !urlLang || !isSettled) return;
    isSynced.current = true;
    if (storedLang !== urlLang) setSettings({ language: urlLang });
  }, [urlLang, isSettled, storedLang, setSettings]);
};
